/* documentRequestLaunch.js — iter-P65 · Universal launch shim.
 *
 * A launch point (Today, Contacts, Contact Profile, Next Best
 * Action, Open Commitments) never opens the RequestDocSheet
 * directly.  Instead it navigates the operator into the Deal
 * Room with a canonical URL query string, and the Deal Room
 * page reads the params on mount to open the SAME drawer that
 * the Current Stage checklist row uses.
 *
 * This preserves the P63/P64 canonical flow verbatim:
 *   • one drawer,
 *   • one create endpoint,
 *   • one reminder cadence,
 *   • one workspace refresh path,
 *   • one activity log entry.
 *
 * There is NO new endpoint, NO new drawer, NO new store slice.
 */

const DOC_KEYWORDS = [
  "document", "documents", "doc", "docs",
  "form", "certificate", "certificates",
  "ta6", "ta10", "epc", "ep",
  "search", "searches",
  "id verification", "id check",
  "management pack", "management-pack",
  "leasehold", "lease",
  "memorandum",
  "invoice", "invoices",
  "signed", "signature", "sign",
  "upload",
];

/** True when the human-readable string mentions something a
 *  Document Request would satisfy.  Case-insensitive, tolerant
 *  of surrounding punctuation. */
export function isDocRequestRelevant(text) {
  if (!text) return false;
  const t = String(text).toLowerCase();
  return DOC_KEYWORDS.some((kw) => t.includes(kw));
}

/** Build the canonical Deal Room URL that auto-opens the
 *  Request Document drawer.  `templateId` is optional; when
 *  provided the drawer lands with the correct template row
 *  pre-selected. */
export function buildRequestDocUrl(dealId, templateId) {
  if (!dealId) return null;
  const base = `/deal-rooms/${encodeURIComponent(dealId)}`;
  const params = new URLSearchParams({ "request-doc": "1" });
  if (templateId) params.set("template", String(templateId));
  return `${base}?${params.toString()}`;
}

/** Convenience: navigate a react-router `navigate` fn into the
 *  Deal Room with the drawer auto-open flag.  Callers pass the
 *  hook's `navigate` fn so we never import react-router here. */
export function openRequestDocument(navigate, dealId, templateId) {
  const url = buildRequestDocUrl(dealId, templateId);
  if (url && typeof navigate === "function") navigate(url);
}

/** True when the given `docRequests` array already has an
 *  ACTIVE row for the given template (`doc_type`).  Used by the
 *  drawer's duplicate-guard so a second launcher for the same
 *  checklist item never creates a phantom duplicate. */
export function hasActiveRequestForTemplate(docRequests, templateId) {
  if (!templateId || !Array.isArray(docRequests)) return false;
  return docRequests.some((r) => {
    if (!r) return false;
    if (String(r.doc_type || "").toLowerCase() !== String(templateId).toLowerCase()) return false;
    const status = String(r.status || "").toLowerCase();
    return status !== "cancelled" && status !== "approved";
  });
}

export const DOCUMENT_REQUEST_LAUNCH_QS_KEY = "request-doc";
export const DOCUMENT_REQUEST_LAUNCH_TEMPLATE_KEY = "template";
